import React, { useState, useEffect } from 'react';
import { 
  Search, Plus, Edit, Eye, EyeOff, Trash2, Clock, DollarSign, 
  ChevronDown, ChevronUp, Filter, X
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';

const ActivitiesProjects = () => {
  // Get API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  const { currentUser } = useAuth();

  // State for projects
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  
  // State for activities
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [activitySearchTerm, setActivitySearchTerm] = useState('');
  const [activityStatusFilter, setActivityStatusFilter] = useState('all');
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Mock data
  useEffect(() => {
    // Mock activities data
    const mockActivities = [
    ];
    
    // Mock projects data
    const mockProjects = [
    ];
    
    setProjects(mockProjects);
    setFilteredProjects(mockProjects);
    setActivities(mockActivities);
    setFilteredActivities(mockActivities);
  }, []);

  // Add this function after your state definitions
useEffect(() => {
  const fetchActivitiesAndProjects = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch activities
      const activitiesResponse = await fetch(`${API_URL}/activities`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Fetch projects
      const projectsResponse = await fetch(`${API_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!activitiesResponse.ok) {
        throw new Error('Failed to fetch activities');
      }
      
      if (!projectsResponse.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const activitiesData = await activitiesResponse.json();
      const projectsData = await projectsResponse.json();
      
      // Transform data to match frontend structure
      const transformedActivities = activitiesData.map(activity => ({
        ...activity,
        isActive: activity.is_active,
        isBillable: activity.is_billable
      }));
      
      const transformedProjects = projectsData.map(project => ({
        ...project,
        isActive: project.is_active,
        billRate: project.bill_rate,
        activityIds: project.activities?.map(activity => activity.id) || []
      }));
      
      setActivities(transformedActivities);
      setFilteredActivities(transformedActivities);
      setProjects(transformedProjects);
      setFilteredProjects(transformedProjects);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to mock data in development
      if (process.env.NODE_ENV === 'development') {
        // Your existing mock data setup...
      }
    } finally {
      setLoading(false);
    }
  };
  
  fetchActivitiesAndProjects();
}, []);
  
  // Filter projects when search term or status filter changes
  useEffect(() => {
    let results = [...projects];
    
    if (projectSearchTerm) {
      const searchTermLower = projectSearchTerm.toLowerCase();
      results = results.filter(project =>
        (project.name || '').toLowerCase().includes(searchTermLower) ||
        (project.description || '').toLowerCase().includes(searchTermLower)
      );
    }
    
    if (projectStatusFilter !== 'all') {
      const isActive = projectStatusFilter === 'active';
      results = results.filter(project => project.isActive === isActive);
    }
    
    setFilteredProjects(results);
  }, [projects, projectSearchTerm, projectStatusFilter]);
  
  // Filter activities when search term or status filter changes
  useEffect(() => {
    let results = [...activities];
    
    if (activitySearchTerm) {
      const searchTermLower = activitySearchTerm.toLowerCase();
      results = results.filter(activity =>
        (activity.name || '').toLowerCase().includes(searchTermLower) ||
        (activity.description || '').toLowerCase().includes(searchTermLower)
      );
    }
    
    if (activityStatusFilter !== 'all') {
      const isActive = activityStatusFilter === 'active';
      results = results.filter(activity => activity.isActive === isActive);
    }
    
    setFilteredActivities(results);
  }, [activities, activitySearchTerm, activityStatusFilter]);
  
  // Project CRUD operations
const addProject = async (project) => {
  try {
    const token = localStorage.getItem('token');
    const apiProject = {
      name: project.name,
      description: project.description,
      bill_rate: project.billRate,
      is_active: project.isActive,
      activity_ids: project.activityIds
    };
    
    const response = await fetch(`${API_URL}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(apiProject)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create project');
    }
    
    const data = await response.json();
    // Transform response data
    const transformedProject = {
      ...data.project,
      isActive: data.project.is_active,
      billRate: data.project.bill_rate,
      activityIds: data.project.activities?.map(activity => activity.id) || []
    };
    
    setProjects([...projects, transformedProject]);
    setShowProjectForm(false);
    setCurrentProject(null);
  } catch (error) {
    console.error('Error creating project:', error);
    alert(`Error: ${error.message}`);
  }
};

const updateProject = async (updatedProject) => {
  try {
    const token = localStorage.getItem('token');
    const apiProject = {
      name: updatedProject.name,
      description: updatedProject.description,
      bill_rate: updatedProject.billRate,
      is_active: updatedProject.isActive,
      activity_ids: updatedProject.activityIds
    };
    
    const response = await fetch(`${API_URL}/projects/${updatedProject.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(apiProject)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update project');
    }
    
    const data = await response.json();
    // Transform response data
    const transformedProject = {
      ...data.project,
      isActive: data.project.is_active,
      billRate: data.project.bill_rate,
      activityIds: data.project.activities?.map(activity => activity.id) || []
    };
    
    setProjects(projects.map(project => 
      project.id === updatedProject.id ? transformedProject : project
    ));
    setShowProjectForm(false);
    setCurrentProject(null);
  } catch (error) {
    console.error('Error updating project:', error);
    alert(`Error: ${error.message}`);
  }
};

  const toggleProjectStatus = async (id) => {
    try {
      const project = projects.find(p => p.id === id);
      if (!project) return;
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !project.isActive })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update project status');
      }
      
      setProjects(projects.map(project => 
        project.id === id ? { ...project, isActive: !project.isActive } : project
      ));
    } catch (error) {
      console.error('Error toggling project status:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const deleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/projects/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete project');
        }
        
        setProjects(projects.filter(project => project.id !== id));
      } catch (error) {
        console.error('Error deleting project:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  // Activity CRUD operations
const addActivity = async (activity) => {
  try {
    const token = localStorage.getItem('token');
    const apiActivity = {
      name: activity.name,
      description: activity.description,
      is_billable: activity.isBillable,
      is_active: activity.isActive
    };
    
    const response = await fetch(`${API_URL}/activities`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(apiActivity)
    });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create activity');
      }
      
      const data = await response.json();
      setActivities([...activities, data.activity]);
      setShowActivityForm(false);
      setCurrentActivity(null);
    } catch (error) {
      console.error('Error creating activity:', error);
      alert(`Error: ${error.message}`);
    }
  };

const updateActivity = async (updatedActivity) => {
  try {
    const token = localStorage.getItem('token');
    const apiActivity = {
      name: updatedActivity.name,
      description: updatedActivity.description,
      is_billable: updatedActivity.isBillable,
      is_active: updatedActivity.isActive
    };
    
    const response = await fetch(`${API_URL}/activities/${updatedActivity.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(apiActivity)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update activity');
    }
    
    const data = await response.json();
    // Transform response data
    const transformedActivity = {
      ...data.activity,
      isActive: data.activity.is_active,
      isBillable: data.activity.is_billable
    };
    
    setActivities(activities.map(activity => 
      activity.id === updatedActivity.id ? transformedActivity : activity
    ));
    setShowActivityForm(false);
    setCurrentActivity(null);
  } catch (error) {
    console.error('Error updating activity:', error);
    alert(`Error: ${error.message}`);
  }
};
  const toggleActivityStatus = async (id) => {
    try {
      const activity = activities.find(a => a.id === id);
      if (!activity) return;
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activities/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !activity.isActive })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update activity status');
      }
      
      setActivities(activities.map(activity => 
        activity.id === id ? { ...activity, isActive: !activity.isActive } : activity
      ));
    } catch (error) {
      console.error('Error toggling activity status:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const deleteActivity = async (id) => {
    if (window.confirm('Are you sure you want to delete this activity? This action cannot be undone.')) {
      try {
        // Check if the activity is associated with any projects
        const associatedProjects = projects.filter(project => 
          project.activityIds.includes(id)
        );
        
        if (associatedProjects.length > 0) {
          if (!window.confirm(`This activity is used in ${associatedProjects.length} projects. Delete anyway?`)) {
            return;
          }
        }
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/activities/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete activity');
        }
        
        // Remove the activity from state
        setActivities(activities.filter(activity => activity.id !== id));
        
        // Also update any projects that had this activity
        const updatedProjects = projects.map(project => ({
          ...project,
          activityIds: project.activityIds.filter(activityId => activityId !== id)
        }));
        setProjects(updatedProjects);
        
      } catch (error) {
        console.error('Error deleting activity:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };
  
  // Helper function to get activity name by ID
  const getActivityNameById = (id) => {
    const activity = activities.find(a => a.id === id);
    return activity ? activity.name : 'Unknown Activity';
  };
  
  // Reset form states
  const resetForms = () => {
    setShowProjectForm(false);
    setShowActivityForm(false);
    setCurrentProject(null);
    setCurrentActivity(null);
  };
  
  // Handle form open for new project
  const handleNewProject = () => {
    setCurrentProject({
      name: '',
      description: '',
      billRate: 0,
      activityIds: [],
      isActive: true
    });
    setShowProjectForm(true);
    setShowActivityForm(false);
  };
  
  // Handle form open for edit project
  const handleEditProject = (project) => {
    setCurrentProject({ ...project });
    setShowProjectForm(true);
    setShowActivityForm(false);
  };
  
  // Handle form open for new activity
  const handleNewActivity = () => {
    setCurrentActivity({
      name: '',
      description: '',
      isBillable: false,
      isActive: true
    });
    setShowActivityForm(true);
    setShowProjectForm(false);
  };
  
  // Handle form open for edit activity
  const handleEditActivity = (activity) => {
    setCurrentActivity({ ...activity });
    setShowActivityForm(true);
    setShowProjectForm(false);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-200">Activities & Projects Management</h2>
      
      {/* Main content area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Projects Panel */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
          <div className="p-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-slate-200">Projects</h3>
            <button 
              onClick={handleNewProject}
              className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                      text-white rounded-md text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Project
            </button>
          </div>
          
          {/* Projects Search and Filters */}
          <div className="p-4 border-b border-slate-700/50 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearchTerm}
                onChange={(e) => setProjectSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md 
                         text-slate-200 text-sm placeholder-slate-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="text-slate-400 w-4 h-4" />
              <span className="text-sm text-slate-400">Status:</span>
              <select
                value={projectStatusFilter}
                onChange={(e) => setProjectStatusFilter(e.target.value)}
                className="bg-slate-700 border border-slate-600 rounded-md text-slate-300 
                         text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          
          {/* Projects List */}
          <div className="p-4">
            {filteredProjects.length === 0 ? (
              <div className="text-center py-4 text-slate-400">
                No projects found. Create a new project to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredProjects.map(project => (
                  <div 
                    key={project.id} 
                    className="bg-slate-700/40 border border-slate-600 rounded-md p-3"
                  >
                    <div className="flex justify-between">
                      <div className="flex items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <h4 className="font-medium text-slate-200 mr-2">{project.name}</h4>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                              ${project.isActive ? 
                                'bg-green-900/30 text-green-300 border border-green-800/50' : 
                                'bg-slate-900/50 text-slate-400 border border-slate-700'}`}
                            >
                              {project.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-400 mb-2">{project.description}</p>
                          <div className="flex space-x-4 text-xs text-slate-400">
                            <div className="flex items-center">
                              <Clock className="w-3.5 h-3.5 mr-1" />
                              <span>{project.hoursLogged} hours</span>
                            </div>
                            <div className="flex items-center">
                              <DollarSign className="w-3.5 h-3.5 mr-1" />
                              <span>${project.billRate}/hr</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEditProject(project)}
                          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleProjectStatus(project.id)}
                          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                          title={project.isActive ? "Hide from dropdowns" : "Show in dropdowns"}
                        >
                          {project.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteProject(project.id)}
                          className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Show associated activities */}
                    {project.activityIds && project.activityIds.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-slate-600/30">
                        <p className="text-xs text-slate-400 mb-1">Associated Activities:</p>
                        <div className="flex flex-wrap gap-1">
                          {project.activityIds.map(activityId => (
                            <span 
                              key={activityId}
                              className="bg-slate-700 px-2 py-0.5 rounded-md text-xs text-slate-300"
                            >
                              {getActivityNameById(activityId)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Activities Panel */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
          <div className="p-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-slate-200">Activities</h3>
            <button 
              onClick={handleNewActivity}
              className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                     text-white rounded-md text-sm"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Activity
            </button>
          </div>
          
          {/* Activities Search and Filters */}
          <div className="p-4 border-b border-slate-700/50 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search activities..."
                value={activitySearchTerm}
                onChange={(e) => setActivitySearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md 
                         text-slate-200 text-sm placeholder-slate-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center space-x-2">
                <Filter className="text-slate-400 w-4 h-4" />
                <span className="text-sm text-slate-400">Status:</span>
                <select
                  value={activityStatusFilter}
                  onChange={(e) => setActivityStatusFilter(e.target.value)}
                  className="bg-slate-700 border border-slate-600 rounded-md text-slate-300 
                           text-sm py-1 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Activities List */}
          <div className="p-4">
            {filteredActivities.length === 0 ? (
              <div className="text-center py-4 text-slate-400">
                No activities found. Create a new activity to get started.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredActivities.map(activity => (
                  <div 
                    key={activity.id} 
                    className="bg-slate-700/40 border border-slate-600 rounded-md p-3"
                  >
                    <div className="flex justify-between">
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-2 mb-1">
                          <h4 className="font-medium text-slate-200">{activity.name}</h4>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            ${activity.isActive ? 
                              'bg-green-900/30 text-green-300 border border-green-800/50' : 
                              'bg-slate-900/50 text-slate-400 border border-slate-700'}`}
                          >
                            {activity.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                            ${activity.isBillable ? 
                              'bg-blue-900/30 text-blue-300 border border-blue-800/50' : 
                              'bg-slate-900/50 text-slate-400 border border-slate-700'}`}
                          >
                            {activity.isBillable ? 'Billable' : 'Non-billable'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mb-2">{activity.description}</p>
                        {/* Show projects that use this activity */}
                        {(() => {
                          const associatedProjects = projects.filter(project => 
                            project.activityIds && project.activityIds.includes(activity.id)
                          );
                          
                          return associatedProjects.length > 0 && (
                            <div className="text-xs text-slate-400">
                              <span className="block mb-1">Used in Projects:</span>
                              <div className="flex flex-wrap gap-1">
                                {associatedProjects.map(project => (
                                  <span 
                                    key={project.id}
                                    className="bg-slate-700 px-2 py-0.5 rounded-md"
                                  >
                                    {project.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                      
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEditActivity(activity)}
                          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleActivityStatus(activity.id)}
                          className="p-1 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                          title={activity.isActive ? "Hide from dropdowns" : "Show in dropdowns"}
                        >
                          {activity.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteActivity(activity.id)}
                          className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Project Form Modal */}
      {showProjectForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">
                {currentProject.id ? 'Edit Project' : 'New Project'}
              </h3>
              <button 
                onClick={resetForms}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              currentProject.id ? updateProject(currentProject) : addProject(currentProject);
            }}>
              <div className="p-4 space-y-4">
                <div>
                  <label htmlFor="projectName" className="block text-sm font-medium text-slate-300 mb-1">
                    Project Name*
                  </label>
                  <input
                    id="projectName"
                    type="text"
                    value={currentProject.name}
                    onChange={(e) => setCurrentProject({...currentProject, name: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                             text-slate-200 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="projectDescription" className="block text-sm font-medium text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id="projectDescription"
                    value={currentProject.description}
                    onChange={(e) => setCurrentProject({...currentProject, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                             text-slate-200 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                
                <div>
                  <label htmlFor="projectBillRate" className="block text-sm font-medium text-slate-300 mb-1">
                    Billing Rate ($/hr)
                  </label>
                  <input
                    id="projectBillRate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentProject.billRate}
                    onChange={(e) => setCurrentProject({...currentProject, billRate: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                             text-slate-200 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Associated Activities
                  </label>
                  <div className="space-y-2 max-h-60 overflow-y-auto bg-slate-700 border border-slate-600 rounded-md p-2">
                    {activities.length === 0 ? (
                      <p className="text-sm text-slate-400">No activities available.</p>
                    ) : (
                      activities.filter(a => a.isActive).map(activity => (
                        <div key={activity.id} className="flex items-center">
                          <input
                            id={`activity-${activity.id}`}
                            type="checkbox"
                            checked={currentProject.activityIds?.includes(activity.id)}
                            onChange={(e) => {
                              const newActivityIds = e.target.checked
                                ? [...(currentProject.activityIds || []), activity.id]
                                : (currentProject.activityIds || []).filter(id => id !== activity.id);
                              setCurrentProject({...currentProject, activityIds: newActivityIds});
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                                     border-slate-500 rounded bg-slate-700"
                          />
                          <label htmlFor={`activity-${activity.id}`} className="ml-2 block text-sm text-slate-300">
                            {activity.name} {activity.isBillable ? '(Billable)' : '(Non-billable)'}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="projectIsActive"
                    type="checkbox"
                    checked={currentProject.isActive}
                    onChange={(e) => setCurrentProject({...currentProject, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                             border-slate-500 rounded bg-slate-700"
                  />
                  <label htmlFor="projectIsActive" className="ml-2 block text-sm text-slate-300">
                    Active
                  </label>
                </div>
              </div>
              
              <div className="p-4 border-t border-slate-700 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForms}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                           rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                           rounded-md transition-colors"
                >
                  {currentProject.id ? 'Update Project' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Activity Form Modal */}
      {showActivityForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">
                {currentActivity.id ? 'Edit Activity' : 'New Activity'}
              </h3>
              <button 
                onClick={resetForms}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              currentActivity.id ? updateActivity(currentActivity) : addActivity(currentActivity);
            }}>
              <div className="p-4 space-y-4">
                <div>
                  <label htmlFor="activityName" className="block text-sm font-medium text-slate-300 mb-1">
                    Activity Name*
                  </label>
                  <input
                    id="activityName"
                    type="text"
                    value={currentActivity.name}
                    onChange={(e) => setCurrentActivity({...currentActivity, name: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                             text-slate-200 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="activityDescription" className="block text-sm font-medium text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id="activityDescription"
                    value={currentActivity.description}
                    onChange={(e) => setCurrentActivity({...currentActivity, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                             text-slate-200 placeholder-slate-400
                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                

                
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <input
                      id="activityIsBillable"
                      type="checkbox"
                      checked={currentActivity.isBillable}
                      onChange={(e) => setCurrentActivity({...currentActivity, isBillable: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                               border-slate-500 rounded bg-slate-700"
                    />
                    <label htmlFor="activityIsBillable" className="ml-2 block text-sm text-slate-300">
                      Billable
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="activityIsActive"
                      type="checkbox"
                      checked={currentActivity.isActive}
                      onChange={(e) => setCurrentActivity({...currentActivity, isActive: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                               border-slate-500 rounded bg-slate-700"
                    />
                    <label htmlFor="activityIsActive" className="ml-2 block text-sm text-slate-300">
                      Active
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t border-slate-700 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForms}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                           rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                           rounded-md transition-colors"
                >
                  {currentActivity.id ? 'Update Activity' : 'Create Activity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesProjects;
